import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { applicationService } from '../../services/applicationService'
import { resumeService } from '../../services/resumeService'
import { jobPostingService } from '../../services/jobPostingService'
import { webhookService } from '../../services/webhookService'
import type { Application, ApplicationStats } from '../../services/applicationService'
import type { ResumeVersion } from '../../services/resumeService'
import type { JobPostingFetchResponse } from '../../services/jobPostingService'
import type { WebhookEvent } from '../../services/webhookService'

const ApplicationsView: React.FC = () => {
  const navigate = useNavigate()
  // State for tracking which application is expanded
  const [expandedApplication, setExpandedApplication] = useState<number | null>(null)
  // State for new application modal
  const [isNewApplicationModalOpen, setIsNewApplicationModalOpen] = useState(false)
  const [newJobTitle, setNewJobTitle] = useState('')
  const [newCompany, setNewCompany] = useState('')
  const [newJobDescription, setNewJobDescription] = useState('')
  
  // State for job posting URL parsing
  const [jobPostingUrl, setJobPostingUrl] = useState('')
  const [isFetchingJobPosting, setIsFetchingJobPosting] = useState(false)
  const [jobPostingStatus, setJobPostingStatus] = useState<string | null>(null)
  const [currentJobPostingId, setCurrentJobPostingId] = useState<string | null>(null)
  const [isFormDisabled, setIsFormDisabled] = useState(false)
  // State for applications data
  const [recentApplications, setRecentApplications] = useState<Application[]>([])
  const [stats, setStats] = useState<ApplicationStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // State for pagination / infinite scroll
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  // State for delete confirmation
  const [deletingApplication, setDeletingApplication] = useState<Application | null>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  // State for resume design flow
  const [, setCreatedApplicationId] = useState<number | null>(null)
  // State for resume versions
  const [resumeVersions, setResumeVersions] = useState<{[key: number]: ResumeVersion[]}>({})
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false)
  const [selectedApplicationForResume, setSelectedApplicationForResume] = useState<number | null>(null)
  
  // State for tracking mouse events for modal closing
  const [mouseDownOutside, setMouseDownOutside] = useState(false)

  // Auto-clear error messages after 15 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null)
      }, 15000) // 15 seconds
      
      return () => clearTimeout(timer)
    }
  }, [error])

  // Change handlers that reset currentJobPostingId when fields are manually edited
  const handleJobTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewJobTitle(e.target.value)
    // If user manually edits the title, reset to manual creation mode
    if (currentJobPostingId) {
      setCurrentJobPostingId(null)
    }
  }

  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewCompany(e.target.value)
    // If user manually edits the company, reset to manual creation mode
    if (currentJobPostingId) {
      setCurrentJobPostingId(null)
    }
  }

  const handleJobDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewJobDescription(e.target.value)
    // If user manually edits the description, reset to manual creation mode
    if (currentJobPostingId) {
      setCurrentJobPostingId(null)
    }
  }

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setJobPostingUrl(e.target.value)
    // If user changes the URL, reset to manual creation mode
    if (currentJobPostingId) {
      setCurrentJobPostingId(null)
    }
  }

  // Helper function to handle enhanced click-outside functionality
  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    // Check if the click is on the backdrop (not on modal content)
    if (e.target === e.currentTarget) {
      setMouseDownOutside(true)
    } else {
      setMouseDownOutside(false)
    }
  }

  const handleBackdropMouseUp = (e: React.MouseEvent, closeFunction: () => void) => {
    // Only close if both mousedown and mouseup were outside the modal
    if (mouseDownOutside && e.target === e.currentTarget) {
      closeFunction()
    }
    setMouseDownOutside(false)
  }

  // Load applications and stats on component mount
  useEffect(() => {
    loadData()
  }, [])

  // Webhook subscription for job posting updates
  useEffect(() => {
    console.log('Webhook useEffect triggered, currentJobPostingId:', currentJobPostingId, 'status:', jobPostingStatus)
    
    if (!currentJobPostingId) {
      console.log('No currentJobPostingId, skipping webhook subscription')
      setJobPostingStatus(null)
      return
    }

    // Only set up webhook subscription if job posting is not already complete
    if (jobPostingStatus === 'complete') {
      console.log('Job posting already complete, skipping webhook subscription')
      return
    }

    console.log(`Setting up webhook subscription for job posting: ${currentJobPostingId}`)
    const unsubscribe = webhookService.subscribeToEntity('job_posting', currentJobPostingId, (event: WebhookEvent) => {
      console.log('Received job posting webhook:', event)
      
      if (event.status === 'fetching') {
        setJobPostingStatus('fetching')
        setIsFormDisabled(true)
      } else if (event.status === 'complete') {
        setJobPostingStatus('complete')
        setIsFormDisabled(false)
        
        // Populate form fields with parsed data
        if (event.data) {
          if (event.data.title) setNewJobTitle(event.data.title)
          if (event.data.company) setNewCompany(event.data.company)
          if (event.data.description) setNewJobDescription(event.data.description)
        }
        
        // Disconnect webhook on completion since we're done with this job posting
        unsubscribe()
      } else if (event.status === 'failed') {
        setJobPostingStatus('failed')
        setIsFormDisabled(false)
        
        // Disconnect webhook on failure since we're done with this job posting
        unsubscribe()
      }
    })

    // Cleanup function to unsubscribe when component unmounts or job posting ID changes
    return () => {
      console.log('Cleaning up webhook subscription for job posting:', currentJobPostingId)
      unsubscribe()
    }

  }, [currentJobPostingId])

  const loadData = async () => {
    try {
      setLoading(true)
      const [applicationsData, statsData] = await Promise.all([
        applicationService.getApplications(0, 50),
        applicationService.getApplicationStats()
      ])
      setRecentApplications(applicationsData)
      setStats(statsData)
      setHasMore(applicationsData.length === 50)
    } catch (err) {
      setError('Failed to load applications data')
      console.error('Error loading data:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return
    try {
      setLoadingMore(true)
      const nextBatch = await applicationService.getApplications(recentApplications.length, 50)
      if (nextBatch.length === 0) {
        setHasMore(false)
      } else {
        setRecentApplications(prev => {
          const existingIds = new Set(prev.map(a => a.id))
          const filtered = nextBatch.filter(a => !existingIds.has(a.id))
          return [...prev, ...filtered]
        })
        if (nextBatch.length < 50) {
          setHasMore(false)
        }
      }
    } catch (err) {
      console.error('Error loading more applications:', err)
    } finally {
      setLoadingMore(false)
    }
  }, [loading, loadingMore, hasMore, recentApplications.length])

  // Attach IntersectionObserver to sentinel for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loadMore])


  const handleStatusChange = async (applicationId: number, statusType: 'online_assessment' | 'interview' | 'rejected') => {
    try {
      const application = recentApplications.find(app => app.id === applicationId)
      if (!application) return

      const updateData = {
        [statusType]: !application[statusType]
      }

      const updatedApplication = await applicationService.updateApplication(applicationId, updateData)
      
      setRecentApplications(prevApplications =>
        prevApplications.map(app =>
          app.id === applicationId ? updatedApplication : app
        )
      )

      // Update stats locally instead of making API call
      if (stats) {
        const newStats = { ...stats }
        
        // Calculate new stats based on the change
        const newValue = updatedApplication[statusType]
        
        if (statusType === 'online_assessment') {
          newStats.online_assessments += newValue ? 1 : -1
        } else if (statusType === 'interview') {
          newStats.interviews += newValue ? 1 : -1
        } else if (statusType === 'rejected') {
          newStats.rejected += newValue ? 1 : -1
        }
        
        // Recalculate rates
        newStats.online_assessment_rate = newStats.total_applications > 0 
          ? Math.round((newStats.online_assessments / newStats.total_applications) * 100 * 10) / 10 
          : 0
        newStats.interview_rate = newStats.total_applications > 0 
          ? Math.round((newStats.interviews / newStats.total_applications) * 100 * 10) / 10 
          : 0
        newStats.rejection_rate = newStats.total_applications > 0 
          ? Math.round((newStats.rejected / newStats.total_applications) * 100 * 10) / 10 
          : 0
        
        setStats(newStats)
      }
    } catch (err) {
      console.error('Error updating application status:', err)
      setError('Failed to update application status')
    }
  }

  const handleToggleApplication = (applicationId: number) => {
    setExpandedApplication(expandedApplication === applicationId ? null : applicationId)
  }

  const handleOpenNewApplicationModal = () => {
    setIsNewApplicationModalOpen(true)
    setNewJobDescription('')
  }

  const handleCloseNewApplicationModal = () => {
    setIsNewApplicationModalOpen(false)
    setNewJobTitle('')
    setNewCompany('')
    setNewJobDescription('')
    
    // Reset job posting state
    handleClearJobPosting()
  }

  // Delete application handlers
  const handleDeleteClick = (application: Application) => {
    setDeletingApplication(application)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deletingApplication) return

    try {
      await applicationService.deleteApplication(deletingApplication.id)
      // Remove the deleted application from the local state
      setRecentApplications(prev => 
        prev.filter(app => app.id !== deletingApplication.id)
      )
      // Reload stats to update counts
      const statsData = await applicationService.getApplicationStats()
      setStats(statsData)
    } catch (err) {
      console.error('Error deleting application:', err)
      setError('Failed to delete application')
    } finally {
      setIsDeleteModalOpen(false)
      setDeletingApplication(null)
    }
  }

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false)
    setDeletingApplication(null)
  }

  const handleViewResume = async (applicationId: number) => {
    try {
      // Check if we already have resume versions for this application
      if (!resumeVersions[applicationId]) {
        const versions = await resumeService.getResumeVersions(applicationId)
        setResumeVersions(prev => ({
          ...prev,
          [applicationId]: versions
        }))
      }
      setSelectedApplicationForResume(applicationId)
      setIsResumeModalOpen(true)
    } catch (error) {
      console.error('Failed to fetch resume versions:', error)
      alert('Failed to load resume versions')
    }
  }

  const handleCloseResumeModal = () => {
    setIsResumeModalOpen(false)
    setSelectedApplicationForResume(null)
  }

  const handleCreateResume = (applicationId: number) => {
    // Navigate to Resume Designer with the application data
    const application = recentApplications.find(app => app.id === applicationId)
    if (application) {
      navigate('/dashboard?view=resume', {
        state: {
          applicationId: applicationId,
          jobDescription: application.job_description
        }
      })
    }
  }

  const handleViewResumePdf = async (resumeId: number) => {
    try {
      await resumeService.viewResumePdf(resumeId)
    } catch (error) {
      console.error('Failed to open PDF:', error)
      alert('Failed to open PDF. Please try again.')
    }
  }

  const handleCreateOptimizedResume = async () => {
    try {
      let createdApplication: Application

      if (currentJobPostingId && jobPostingStatus === 'complete') {
        // Create application from existing job posting
        createdApplication = await applicationService.createApplicationFromJobPosting(currentJobPostingId)
      } else {
        // Create job posting first, then application
        const jobPostingData = {
          title: newJobTitle || 'New Position',
          company: newCompany || 'Target Company',
          description: newJobDescription
        }

        const jobPosting = await jobPostingService.createJobPosting(jobPostingData)
        createdApplication = await applicationService.createApplicationFromJobPosting(jobPosting.id)
      }

      setCreatedApplicationId(createdApplication.id)
      
      // Close the modal
      handleCloseNewApplicationModal()
      
      // Navigate to Resume Designer with the job description and application ID
      navigate('/dashboard?view=resume', { 
        state: { 
          jobDescription: newJobDescription,
          applicationId: createdApplication.id 
        } 
      })
      
    } catch (error: any) {
      console.error('Failed to create application for resume design:', error)
      
      // Check if it's a duplicate application error
      if (error?.response?.status === 409) {
        alert('You have already created an application for this job posting. Please try a different job posting.')
      } else {
        alert('Failed to create application. Please try again.')
      }
    }
  }

  const handleAddToApplications = async () => {
    try {
      let newApplication: Application

      if (currentJobPostingId && jobPostingStatus === 'complete') {
        // Create application from existing job posting
        newApplication = await applicationService.createApplicationFromJobPosting(currentJobPostingId)
      } else {
        // Create job posting first, then application
        const jobPostingData = {
          title: newJobTitle || 'New Job Application',
          company: newCompany || 'Unknown Company',
          description: newJobDescription
        }

        const jobPosting = await jobPostingService.createJobPosting(jobPostingData)
        newApplication = await applicationService.createApplicationFromJobPosting(jobPosting.id)
      }
      
      // Add the new application to the list
      setRecentApplications(prevApplications => [newApplication, ...prevApplications])
      
      // Reload stats after adding
      const statsData = await applicationService.getApplicationStats()
      setStats(statsData)
      
      // Close the modal
      handleCloseNewApplicationModal()
    } catch (err: any) {
      console.error('Error creating application:', err)
      
      // Check if it's a duplicate application error
      if (err?.response?.status === 409) {
        setError('You have already created an application for this job posting. Please try a different job posting.')
      } else {
        setError('Failed to create application. Please try again.')
      }
      
      // Close the modal even on error
      handleCloseNewApplicationModal()
    }
  }

  const handleFetchJobPosting = async () => {
    if (!jobPostingUrl.trim()) {
      return
    }

    console.log('Starting job posting fetch for URL:', jobPostingUrl.trim())
    try {
      setIsFetchingJobPosting(true)
      setJobPostingStatus(null)
      setIsFormDisabled(true) // Disable form fields immediately when parsing starts
      
      // Call the job posting fetch API
      const response: JobPostingFetchResponse = await jobPostingService.fetchJobPosting({
        url: jobPostingUrl.trim(),
        source: 'web-ui'
      })
      
      console.log('Job posting fetch response:', response)
      
      // Handle different response statuses
      if (response.status === 'complete') {
        // Job posting is already complete, fetch the full data immediately
        console.log('Job posting already complete, fetching full data...')
        try {
          const jobPostingData = await jobPostingService.getJobPosting(response.job_posting_id)
          console.log('Fetched complete job posting data:', jobPostingData)
          
          // Populate form fields with parsed data
          if (jobPostingData.title) setNewJobTitle(jobPostingData.title)
          if (jobPostingData.company) setNewCompany(jobPostingData.company)
          if (jobPostingData.description) setNewJobDescription(jobPostingData.description)
          
          // Set status and enable form fields
          setJobPostingStatus('complete')
          setIsFormDisabled(false)
          setCurrentJobPostingId(response.job_posting_id)
          setIsFetchingJobPosting(false) // Stop loading state
          
          console.log('Job posting data populated successfully')
        } catch (err) {
          console.error('Error fetching complete job posting data:', err)
          setIsFormDisabled(false)
          setIsFetchingJobPosting(false) // Stop loading state on error
        }
      } else {
        // Job posting is being parsed, set up webhook subscription
        console.log('Job posting is being parsed, setting up webhook subscription')
        setCurrentJobPostingId(response.job_posting_id)
        setJobPostingStatus('pending')
        setIsFetchingJobPosting(false) // Stop loading state, webhook will handle completion
      }
      
      console.log('Set currentJobPostingId to:', response.job_posting_id)
      
    } catch (err: any) {
      console.error('Error fetching job posting:', err)
      setIsFetchingJobPosting(false)
      setIsFormDisabled(false) // Re-enable form fields on error
    }
  }

  const handleClearJobPosting = () => {
    setJobPostingUrl('')
    setJobPostingStatus(null)
    setCurrentJobPostingId(null)
    setIsFormDisabled(false)
    setIsFetchingJobPosting(false)
    
    // Clear form fields
    setNewJobTitle('')
    setNewCompany('')
    setNewJobDescription('')
  }

  const canCreateApplication = (): boolean => {
    // Can create if job posting parsing is complete
    if (currentJobPostingId && jobPostingStatus === 'complete') {
      return true
    }
    
    // Can create if manual data is provided
    return newJobTitle.trim() !== '' && newCompany.trim() !== '' && newJobDescription.trim() !== ''
  }

  // Use API stats or fallback to calculated stats
  const currentStats = stats || {
    total_applications: recentApplications.length,
    online_assessments: recentApplications.filter(app => app.online_assessment).length,
    interviews: recentApplications.filter(app => app.interview).length,
    rejected: recentApplications.filter(app => app.rejected).length,
    online_assessment_rate: 0,
    interview_rate: 0,
    rejection_rate: 0
  }

  const statsDisplay = [
    {
      title: 'Applications Submitted',
      value: currentStats.total_applications,
      percentage: null,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      icon: '📝'
    },
    {
      title: 'Online Assessments',
      value: currentStats.online_assessments,
      percentage: currentStats.online_assessment_rate,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      icon: '💻'
    },
    {
      title: 'Interviews',
      value: currentStats.interviews,
      percentage: currentStats.interview_rate,
      color: 'text-sky-600',
      bgColor: 'bg-sky-50',
      icon: '🎯'
    },
    {
      title: 'Rejected',
      value: currentStats.rejected,
      percentage: currentStats.rejection_rate,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      icon: '❌'
    }
  ]

  if (loading) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Applications</h2>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading applications...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Applications</h2>
      
      {/* Error message display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md flex justify-between items-center">
          <div className="flex items-center">
            <span>{error}</span>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-600 ml-4"
            aria-label="Close error message"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsDisplay.map((stat, index) => (
          <div key={index} className={`${stat.bgColor} rounded-lg p-6 border border-gray-200`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                <div className="flex items-baseline gap-2">
                  <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
                  {stat.percentage !== null && (
                    <p className={`text-sm font-medium ${stat.color} opacity-75`}>
                      ({stat.percentage}%)
                    </p>
                  )}
                </div>
              </div>
              <div className="text-2xl">
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Applications List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Recent Applications</h3>
          <button
            onClick={handleOpenNewApplicationModal}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
          >
            New Application
          </button>
        </div>
        <div className="divide-y divide-gray-200">
          {recentApplications.map((application, index) => (
            <div key={index}>
              <div className="px-6 py-4 hover:bg-gray-50 transition-colors duration-200">
                {/* Desktop Layout */}
                <div className="hidden md:flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2 flex-wrap">
                      <h4 className="text-lg font-medium text-gray-900">{application.job_title}</h4>
                      <span className="text-sm text-gray-500">at</span>
                      <span className="text-lg font-medium text-gray-700">{application.company}</span>
                      {application.has_resume && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                          📄 {application.resume_count || 1} Resume{application.resume_count && application.resume_count > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    
                    {/* Status Checkboxes */}
                    <div className="flex items-center gap-6 mb-3">
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={application.online_assessment}
                          onChange={() => handleStatusChange(application.id, 'online_assessment')}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span>Online Assessment</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={application.interview}
                          onChange={() => handleStatusChange(application.id, 'interview')}
                          className="w-4 h-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                        />
                        <span>Interview</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={application.rejected}
                          onChange={() => handleStatusChange(application.id, 'rejected')}
                          className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                        />
                        <span>Rejected</span>
                      </label>
                    </div>
                    
                    <div className="text-sm text-gray-500">
                      Applied on {new Date(application.applied_date).toLocaleDateString()}
                    </div>
                  </div>
                  
                  {/* Action Buttons - Desktop */}
                  <div className="flex items-center gap-2 ml-6">
                    <button 
                      onClick={() => handleToggleApplication(application.id)}
                      className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                    >
                      {expandedApplication === application.id ? 'Hide Application' : 'Show Application'}
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(application)}
                      className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors duration-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Mobile Layout */}
                <div className="md:hidden">
                  <div className="mb-3">
                    <h4 className="text-lg font-medium text-gray-900 mb-1">{application.job_title}</h4>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-gray-500">at</span>
                      <span className="text-lg font-medium text-gray-700">{application.company}</span>
                      {application.has_resume && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                          📄 {application.resume_count || 1} Resume{application.resume_count && application.resume_count > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Status Checkboxes - Mobile */}
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={application.online_assessment}
                        onChange={() => handleStatusChange(application.id, 'online_assessment')}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span>Assessment</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={application.interview}
                        onChange={() => handleStatusChange(application.id, 'interview')}
                        className="w-4 h-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                      />
                      <span>Interview</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={application.rejected}
                        onChange={() => handleStatusChange(application.id, 'rejected')}
                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                      />
                      <span>Rejected</span>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      Applied on {new Date(application.applied_date).toLocaleDateString()}
                    </div>
                    
                    {/* Action Buttons - Mobile */}
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleToggleApplication(application.id)}
                        className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                      >
                        {expandedApplication === application.id ? 'Hide' : 'Show'}
                      </button>
                      <button 
                        onClick={() => handleDeleteClick(application)}
                        className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Expandable Job Description Section */}
              {expandedApplication === application.id && (
                <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="text-sm font-semibold text-gray-700">Job Description</h5>
                    <button
                      onClick={() => handleViewResume(application.id)}
                      className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                    >
                      View Resume
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                    {application.job_description}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Infinite Scroll Sentinel & Loading Indicator */}
        <div ref={sentinelRef} className="py-4 text-center border-t border-gray-100">
          {loadingMore && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading more applications...</span>
            </div>
          )}
          {!hasMore && recentApplications.length > 0 && (
            <p className="text-xs text-gray-400 py-2">All {recentApplications.length} applications loaded</p>
          )}
        </div>
      </div>

      {/* New Application Modal */}
      {isNewApplicationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop with blur effect */}
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
            onMouseDown={handleBackdropMouseDown}
            onMouseUp={(e) => handleBackdropMouseUp(e, handleCloseNewApplicationModal)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">New Job Application</h3>
              <button
                onClick={handleCloseNewApplicationModal}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-4 flex-1 overflow-y-auto">
              <div className="space-y-4">
                {/* Job Posting URL Input */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <h4 className="text-sm font-semibold text-blue-900">Parse Job Posting from URL</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="jobPostingUrl" className="block text-sm font-medium text-gray-700 mb-2">
                        Job Posting URL
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="url"
                          id="jobPostingUrl"
                          value={jobPostingUrl}
                          onChange={handleUrlChange}
                          placeholder="https://company.com/job-posting"
                          disabled={isFetchingJobPosting || isFormDisabled}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <button
                          onClick={handleFetchJobPosting}
                          disabled={!jobPostingUrl.trim() || isFetchingJobPosting || jobPostingStatus === 'complete'}
                          className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-200 flex items-center gap-2 ${
                            jobPostingStatus === 'complete' 
                              ? 'bg-green-600 hover:bg-green-700' 
                              : 'bg-blue-600 hover:bg-blue-700'
                          } disabled:bg-gray-300 disabled:cursor-not-allowed`}
                        >
                          {isFetchingJobPosting ? (
                            <>
                              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Fetching...
                            </>
                          ) : jobPostingStatus === 'complete' ? (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Done
                            </>
                          ) : (
                            'Fetch Job Details'
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Status and Error Display */}
                    {jobPostingStatus && (
                      <div className="flex items-center gap-2 text-sm">
                        {jobPostingStatus === 'pending' && (
                          <>
                            <svg className="w-4 h-4 text-yellow-600 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            <span className="text-yellow-700">Parsing job posting...</span>
                          </>
                        )}
                        {jobPostingStatus === 'fetching' && (
                          <>
                            <svg className="w-4 h-4 text-blue-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            <span className="text-blue-700">Extracting job details...</span>
                          </>
                        )}
                        {jobPostingStatus === 'complete' && (
                          <>
                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-green-700">Job details extracted successfully!</span>
                          </>
                        )}
                        {jobPostingStatus === 'failed' && (
                          <>
                            <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <span className="text-red-700">Failed to parse job posting. Please input data manually.</span>
                          </>
                        )}
                      </div>
                    )}
                    
                    {(jobPostingStatus === 'complete' || jobPostingStatus === 'failed') && (
                      <button
                        onClick={handleClearJobPosting}
                        className="text-sm text-gray-600 hover:text-gray-800 underline"
                      >
                        Clear and start over
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Divider */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 border-t border-gray-200"></div>
                  <span className="text-sm text-gray-500 font-medium">OR</span>
                  <div className="flex-1 border-t border-gray-200"></div>
                </div>
                
                {/* Manual Input Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-2">
                      Job Title *
                    </label>
                    <input
                      type="text"
                      id="jobTitle"
                      value={newJobTitle}
                      onChange={handleJobTitleChange}
                      placeholder="e.g., Software Engineer"
                      disabled={isFormDisabled}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company *
                    </label>
                    <input
                      type="text"
                      id="company"
                      value={newCompany}
                      onChange={handleCompanyChange}
                      placeholder="e.g., Google"
                      disabled={isFormDisabled}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="jobDescription" className="block text-sm font-medium text-gray-700 mb-2">
                    Job Description *
                  </label>
                  <textarea
                    id="jobDescription"
                    value={newJobDescription}
                    onChange={handleJobDescriptionChange}
                    placeholder="Paste the job description here..."
                    disabled={isFormDisabled}
                    className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <button
                onClick={handleCloseNewApplicationModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleAddToApplications}
                  disabled={!canCreateApplication()}
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
                >
                  Add to Applications
                </button>
                <button
                  onClick={handleCreateOptimizedResume}
                  disabled={!canCreateApplication()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
                >
                  Create Optimized Resume
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && deletingApplication && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onMouseDown={handleBackdropMouseDown}
            onMouseUp={(e) => handleBackdropMouseUp(e, handleCancelDelete)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Application</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Are you sure you want to delete the application for{' '}
                <span className="font-medium">{deletingApplication.job_title}</span> at{' '}
                <span className="font-medium">{deletingApplication.company}</span>?
              </p>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 transition-colors duration-200"
              >
                Delete Application
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume Versions Modal */}
      {isResumeModalOpen && selectedApplicationForResume && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onMouseDown={handleBackdropMouseDown}
            onMouseUp={(e) => handleBackdropMouseUp(e, handleCloseResumeModal)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Resume Versions</h3>
                <p className="text-sm text-gray-500">
                  {recentApplications.find(app => app.id === selectedApplicationForResume)?.job_title} at{' '}
                  {recentApplications.find(app => app.id === selectedApplicationForResume)?.company}
                </p>
              </div>
              <button
                onClick={handleCloseResumeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-4 max-h-96 overflow-y-auto">
              {resumeVersions[selectedApplicationForResume]?.length > 0 ? (
                <div className="space-y-3">
                  {resumeVersions[selectedApplicationForResume].map((resume) => (
                    <div key={resume.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-900">{resume.title}</h4>
                        <p className="text-sm text-gray-500">
                          Template: {resume.template_used} • Created: {new Date(resume.created_at).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true })}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {resume.has_pdf ? (
                          <button
                            onClick={() => handleViewResumePdf(resume.id)}
                            className="px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors duration-200"
                          >
                            View PDF
                          </button>
                        ) : (
                          <span className="px-3 py-1.5 text-sm font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded-lg">
                            No PDF Available
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-4">📄</div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No Resume Versions</h4>
                  <p className="text-gray-500 mb-4">No resumes have been generated for this application yet.</p>
                  <button
                    onClick={() => handleCreateResume(selectedApplicationForResume)}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    Create Resume
                  </button>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleCloseResumeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ApplicationsView
